<script setup>
import { useUsersStore, useUserGroupsStore } from "@/stores"
import { emailValidator, requiredValidator } from "@validators"
import axios from "@axios"
import { computed, reactive, ref, watch } from "vue"
import { useRoute } from "vue-router"
import rmLogo from '@/assets/logo.svg'
import PreferredRequestInfoDialog from '@core/components/preferredRequestInfoDialog.vue'
import CmsText from '@core/components/CmsText.vue'
import ApplicationStepFields from '@core/components/application/ApplicationStepFields.vue'
import { safeRedirectPath } from '@/utils/safeRedirect'
import {
  PATH_FOR_FLOW,
  STEP_TITLES,
  applicationPayload,
  builtInForm,
} from '@/utils/applicationForm'

const emit = defineEmits([
  'update:isDialogVisible',
  'update:restaurant',
  'confirm',
  'cancel'
])
const route = useRoute()
const userGroupsStore = useUserGroupsStore()
let guestUserGroup;
({ guestUserGroup } = storeToRefs(userGroupsStore))

const usersStore = useUsersStore()
let myUser = null
let loginDialogMode = null
let loginDialogVerificationType = null
let isShowingPreferredRequestInfoDialog = null
let personalAdminInvitation = null;
({ myUser, loginDialogMode, loginDialogVerificationType, isShowingPreferredRequestInfoDialog, personalAdminInvitation } = storeToRefs(usersStore))

const refVForm = ref()

// const formMode = ref(lastPathSegment || (userPreviouslyAuthenticated ? 'login' : 'join'))
// const showForm = ref((lastPathSegment === 'join' || lastPathSegment === 'login' || userPreviouslyAuthenticated))
const showFormComplete = ref(false)

const formSumbitted = ref(false)

const email = ref(route?.query?.email || '')
const password = ref('')
const termsAccepted = ref(true)
const reviewedRM = ref(true)
const isPasswordVisible = ref(false)
const referredBy = ref(route?.query?.referredBy || '')
const referredByEmail = ref(route?.query?.referredByEmail || '')

// RM-427: the application's answers, by question key. Which questions are
// asked (and which are required) comes from the server's question library for
// this path and group (GET /application/form).
const answers = reactive({})
const smsOptIn = ref(false)
const applicationForm = ref(null)
const applicationFormLoading = ref(false)

// Screen 1 is the welcome; the form's own screens follow it.
const currentStep = ref(1)

// Treat gift-specific fields as part of the request flow unless the dialog is in login mode
const isGiftRequestFlow = computed(() => (loginDialogVerificationType.value === 'gift' || loginDialogVerificationType.value === 'spouse') && loginDialogMode.value !== 'login')

// Only show the "Request to Join" CTA when the user landed via friends.vue ('gift'/'spouse'),
// activate.vue ('activate'), join.vue ('group'), or a "Request Free Restaurant Account"
// entry point ('restaurant'). Generic entry points (e.g. HomeMasthead) leave
// loginDialogVerificationType unset and should not display this button.
const canRequestToJoin = computed(() => ['gift', 'spouse', 'activate', 'group', 'restaurant'].includes(loginDialogVerificationType.value))

// A real, authenticated member (dummy users are treated as anonymous here,
// mirroring the dummyEmail handling in register()/login()).
const isLoggedIn = computed(() => !!myUser.value && myUser.value.userLevel !== 'dummy')

// Already-logged-in members who click "Request Free Restaurant Account" can't
// run the anonymous join wizard — instead show CMS copy telling them to email in.
const isLoggedInRestaurantRequest = computed(() => loginDialogVerificationType.value === 'restaurant' && isLoggedIn.value)

// RM-150: personal-admin invitees use a single-page form instead of the
// 5-step activate wizard. The invitation token (set by pages/[...all]/personal-admin.vue)
// pre-fills the locked email and is sent through with the register POST.
const isPersonalAdminFlow = computed(() => loginDialogVerificationType.value === 'personal-admin')
watch(personalAdminInvitation, invitation => {
  if (invitation && invitation.adminEmail) {
    email.value = invitation.adminEmail
    answers.email = invitation.adminEmail
  }
}, { immediate: true })

// Watch for guestUserGroup changes and pre-populate referrer field
watch(() => guestUserGroup.value, newValue => {
  if (!referredBy.value && newValue) {
    // For friends groups (gift pages), use the individual's name
    if (newValue.referrerFirstName && newValue.referrerLastName) {
      referredBy.value = `${newValue.referrerFirstName} ${newValue.referrerLastName}`
    }

    // For discount/enterprise groups (join/activate pages), use the group name
    else if (newValue.name && (loginDialogVerificationType.value === 'group' || loginDialogVerificationType.value === 'activate')) {
      referredBy.value = newValue.name
    }
  }
  if (!answers.referredBy && referredBy.value)
    answers.referredBy = referredBy.value
}, { immediate: true })

// Answers typed into the form (the email also feeds the log-in screen).
const setAnswer = (key, value) => {
  answers[key] = value
  if (key === 'email')
    email.value = value
}

if (email.value && !answers.email)
  answers.email = email.value

// Leaving the invite page means starting the session over (Kapil, RM-241).
// The slug flows' verificationType is sticky store state; without this, a
// member who navigated off their /{slug}/(activate|join|friends|spouse) page
// mid-wizard could submit with lost group context (prod: Emily Viola / Shore).
// On route change away from the flow's page type, reset the join flow — the
// logged-out localStorage funnel (RM-223) still guides them back to the group
// page to begin again. Typed form fields are kept; only flow state resets.
const SLUG_FLOW_PAGE_SUFFIX = {
  activate: '/activate',
  group: '/join',
  gift: '/friends',
  spouse: '/spouse',
}

watch(() => route.path, newPath => {
  const suffix = SLUG_FLOW_PAGE_SUFFIX[loginDialogVerificationType.value]
  if (!suffix)
    return
  if (!(newPath || '').toLowerCase().endsWith(suffix)) {
    loginDialogVerificationType.value = null
    if (loginDialogMode.value === 'activate')
      loginDialogMode.value = false
    currentStep.value = 1
  }
})

// Form Errors
const errors = ref({
  email: undefined,
  password: undefined,
  termsAccepted: undefined,
  reviewedRM: undefined
})

const updateModelValue = val => {
  emit('update:isDialogVisible', val)
}
const onClose = () => {
  loginDialogMode.value = false
  loginDialogVerificationType.value = null
  formSumbitted.value = false
  currentStep.value = 1
  console.log(guestUserGroup.value)
}

const clearErrors = () => {
  if (errors.value) {
    errors.value = {}
  }
}

const onSubmitJoin = () => {
  clearErrors()

  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid)
      register()
  })
}

// Navigate to next step with validation where needed
const checkingEmail = ref(false)

const goToNextStep = async () => {
  // Steps with no required fields - just advance
  if (currentStep.value === 1) {
    currentStep.value++

    return
  }

  // Steps with required fields - validate first
  clearErrors()
  const { valid: isValid } = await refVForm.value?.validate()
  if (!isValid) return

  // On the screen that asks for the email, check it isn't in use before advancing
  const emailOnThisScreen = (applicationForm.value?.questions || [])
    .some(q => q.key === 'email' && q.step === currentFormStep.value)
  if (emailOnThisScreen && answers.email) {
    checkingEmail.value = true
    try {
      const { data } = await axios.post('/auth/check-email', { email: answers.email })
      if (!data.available) {
        errors.value = { ...errors.value, email: ['This email is already in use.'] }
        checkingEmail.value = false

        return
      }
    } catch (e) {
      console.error('Email check failed:', e)
    } finally {
      checkingEmail.value = false
    }
  }

  currentStep.value++
}

// Go back one step
const goToPrevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}


const onSubmitLogin = () => {
  clearErrors()

  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid)
      login()
  })
}


// The wizard's flow type (loginDialogVerificationType) is sticky Pinia state,
// but the group/profile slug was re-read from the CURRENT route at every submit.
// Finishing the wizard after navigating off the originating
// /{slug}/{join|activate|friends|spouse} page (e.g. from the home page) lost the
// slug: enterprise activate signups were silently created with no group (no
// pending roster entry + blank group content in the welcome email), and the
// other slug flows threw on route.params.all[0]. The group loaded when the
// member first hit the page is still in the store, so trust it whenever it
// belongs to this flow (joinUrl suffix check) and only fall back to the current
// route otherwise — a foreign marketing-page slug would no-op server-side.
const resolveSignupPublicId = flowSuffix => {
  const storedGroup = guestUserGroup.value
  if (storedGroup?.publicId && storedGroup.joinUrl?.endsWith(flowSuffix))
    return storedGroup.publicId

  return route.params.all?.[0] || null
}

// When neither source resolves we refuse to submit rather than create a
// group-less account (which admins would have to repair by hand): the member
// is told to reopen their original link. Rendered above the submit buttons.
const rejectLostSignupLink = () => {
  errors.value = {
    ...errors.value,
    signupLink: ['We lost track of which group or invitation you came from. Please reopen your original signup link and try again.'],
  }
}

// ── RM-427: the application for this path and group ─────────────────────────
const applicationPath = computed(() => PATH_FOR_FLOW[loginDialogVerificationType.value] || null)

const applicationPublicId = () => {
  if (isPersonalAdminFlow.value)
    return (personalAdminInvitation.value?.memberProfileId || '').toLowerCase() || null
  const suffix = SLUG_FLOW_PAGE_SUFFIX[loginDialogVerificationType.value]

  return suffix ? resolveSignupPublicId(suffix) : null
}

let applicationFormRequest = 0

const loadApplicationForm = async () => {
  const path = applicationPath.value
  if (!path) {
    applicationForm.value = null

    return
  }
  const requestId = ++applicationFormRequest
  applicationFormLoading.value = true
  try {
    const { data } = await axios.get('/application/form', { params: { path, publicId: applicationPublicId() || undefined } })
    if (requestId === applicationFormRequest)
      applicationForm.value = data
  } catch (e) {
    // Never block an application: fall back to the form as it was before RM-427.
    console.error('Application form failed to load; using the built-in form', e)
    if (requestId === applicationFormRequest)
      applicationForm.value = builtInForm(path)
  } finally {
    if (requestId === applicationFormRequest)
      applicationFormLoading.value = false
  }
}

watch(
  [() => loginDialogMode.value, () => loginDialogVerificationType.value, () => personalAdminInvitation.value],
  ([mode]) => {
    if (mode && mode !== 'login' && mode !== 'join')
      loadApplicationForm()
  },
  { immediate: true },
)

const formSteps = computed(() => applicationForm.value?.steps || [])
const wizardStepCount = computed(() => formSteps.value.length + 1)
const currentFormStep = computed(() => (currentStep.value >= 2 ? formSteps.value[currentStep.value - 2] || null : null))
const isLastStep = computed(() => currentStep.value >= wizardStepCount.value)

const questionsOnScreen = step => (applicationForm.value?.questions || []).filter(q => q.step === step)

// "Optional: ..." only while every social question is optional.
const socialAllOptional = computed(() => !questionsOnScreen('social').some(q => q.required))

const favoritesHint = computed(() => {
  const q = (applicationForm.value?.questions || []).find(x => x.type === 'restaurants')

  return q?.hint || ''
})

// The server's errors for answers on an earlier screen (shown with the submit
// button, since that screen's fields aren't visible any more).
const serverFieldErrors = computed(() => {
  const all = errors.value || {}

  return (applicationForm.value?.questions || [])
    .filter(q => q.step !== currentFormStep.value && Array.isArray(all[q.key]) && all[q.key].length)
    .map(q => `${q.label}: ${all[q.key][0]}`)
})

// RM-427: the application's answers (by question key, plus the field names
// the server has always read) and the RM via text choice.
const applicationData = () => {
  const form = applicationForm.value || builtInForm(applicationPath.value || 'activate')
  const payload = applicationPayload(form, answers)

  return {
    ...payload,
    // a hidden "Who referred you?" still carries the page's referrer
    referredBy: payload.referredBy || referredBy.value || undefined,
    smsOptIn: !!(form.smsOptIn && form.smsOptIn.enabled && smsOptIn.value),
  }
}

const register = () => {
  let url = '/auth/register'
  let data
  let thePublicId

  if (loginDialogVerificationType.value == "platinumtrial") {
    data = {
      email: email.value,
      password: password.value,
      termsAccepted: termsAccepted.value,
      promptToVerify: true,
      verificationType: "platinumtrial",
      verificationInfo1Label: "Who referred you?",
      verifierName: "RM",
      platinumPlanIdToGiveAfterPlatinumApproval: 'individual-1cycle-3month',
      pendingPlatinumApproval: true,
      referredBy: referredBy.value,
      referredByEmail: referredByEmail.value,

    }
  } else if (loginDialogVerificationType.value == "partner") {
    data = {
      email: email.value,
      password: password.value,
      termsAccepted: termsAccepted.value,
      promptToVerify: true,
      verificationType: "partner",
      verificationInfo1Label: 'Who referred you?',
      verificationInfo2Label: 'Restaurant/group or Membership club',
      platinumPlanIdToGiveAfterVerification: 'individual-1cycle-annual',
      referredBy: referredBy.value,
      referredByEmail: referredByEmail.value,

    }
  } else if (loginDialogVerificationType.value == "gift") {
    thePublicId = resolveSignupPublicId('/friends')
    if (!thePublicId) {
      rejectLostSignupLink()

      return
    }
    userGroupsStore.getGuestUserGroup(thePublicId, "friends")
    url = '/auth/register/' + thePublicId.toLowerCase()
    data = {
      ...applicationData(),

      // Random UUID to satisfy passport - backend will discard and generate its own secure password
      password: crypto.randomUUID(),
      termsAccepted: termsAccepted.value,
      verificationType: "gift",
      platinumPlanIdToGiveAfterVerification: guestUserGroup.value?.trialPlanId,
      referredByEmail: referredByEmail.value,
    }
  } else if (loginDialogVerificationType.value == "spouse") {
    thePublicId = resolveSignupPublicId('/spouse')
    if (!thePublicId) {
      rejectLostSignupLink()

      return
    }
    userGroupsStore.getGuestUserGroup(thePublicId, "spouse")
    url = '/auth/register/' + thePublicId.toLowerCase()
    data = {
      ...applicationData(),

      // Random UUID to satisfy passport - backend will discard and generate its own secure password
      password: crypto.randomUUID(),
      termsAccepted: termsAccepted.value,
      verificationType: "spouse",
      platinumPlanIdToGiveAfterVerification: guestUserGroup.value?.trialPlanId,
      referredByEmail: referredByEmail.value,
    }
  } else if (loginDialogVerificationType.value == "group") {
    // IMPORTANT: Check for "group" BEFORE generic "activate" check because
    // loginDialogMode may be 'activate' (set by HomeMasthead button) even when
    // loginDialogVerificationType is 'group' (set by join.vue page)
    thePublicId = resolveSignupPublicId('/join')
    if (!thePublicId) {
      rejectLostSignupLink()

      return
    }
    userGroupsStore.getGuestUserGroup(thePublicId, "discount")
    url = '/auth/register/' + thePublicId.toLowerCase()
    data = {
      ...applicationData(),

      // Random UUID to satisfy passport - backend will discard and generate its own secure password
      password: crypto.randomUUID(),
      termsAccepted: termsAccepted.value,
      reviewedRM: reviewedRM.value,
      verificationType: "group",
      platinumPlanIdToGiveAfterVerification: guestUserGroup.value?.trialPlanId,
      referredByEmail: referredByEmail.value,
    }
  } else if (loginDialogVerificationType.value == "personal-admin") {
    // RM-150: token-authenticated invite. Backend validates invitationToken
    // before registering, then sets emailConfirmed/accountApproved/verified
    // and skips the verify-email round trip.
    const invitation = personalAdminInvitation.value || {}
    const memberProfileId = (invitation.memberProfileId || '').toLowerCase()
    if (!memberProfileId) {
      errors.value = { ...errors.value, invitationToken: ['Invitation context is missing — please reopen the invitation link.'] }

      return
    }
    url = '/auth/register/' + memberProfileId
    data = {
      ...applicationData(),
      email: invitation.adminEmail || answers.email,
      password: password.value,
      termsAccepted: termsAccepted.value,
      reviewedRM: reviewedRM.value,
      verificationType: 'personal-admin',
      invitationToken: invitation.token,
    }
  } else if (loginDialogVerificationType.value == "activate" || loginDialogMode.value == "activate") {
    // Only get publicId if loginDialogVerificationType is explicitly "activate" (enterprise activate page)
    // Don't use route params when just loginDialogMode == "activate" (from HomeMasthead on home page)
    if (loginDialogVerificationType.value == "activate") {
      thePublicId = resolveSignupPublicId('/activate')
      if (!thePublicId) {
        rejectLostSignupLink()

        return
      }
      userGroupsStore.getGuestUserGroup(thePublicId, "enterprise")
      url = '/auth/register/' + thePublicId.toLowerCase()
    } else {
      url = '/auth/register'
    }
    data = {
      ...applicationData(),

      // Random UUID to satisfy passport - backend will discard and generate its own secure password
      password: crypto.randomUUID(),
      termsAccepted: termsAccepted.value,
      reviewedRM: reviewedRM.value,

      // Only set verificationType to 'activate' for enterprise activate pages, not home page
      // 'restaurant' tags the "Request Free Restaurant Account" flow so the backend can
      // route to the requestRestaurantAccess email template.
      verificationType: loginDialogVerificationType.value == "activate"
        ? 'activate'
        : loginDialogVerificationType.value == "restaurant"
          ? 'restaurant'
          : null,
    }
  } else {
    data = {
      email: email.value,
      password: password.value,
      termsAccepted: termsAccepted.value,
    }
  }

  //if dummy user pass though dummyEmail
  if (myUser?.value?.userLevel == 'dummy') {
    data.dummyEmail = myUser?.value?.email
  }

  console.log('Sending registration data:', JSON.stringify(data, null, 2))
  console.log('URL:', url)

  axios.post(url, data).then(r => {
    console.log('Registration successful, checking flow type...')
    console.log('loginDialogVerificationType:', loginDialogVerificationType.value)
    console.log('loginDialogMode:', loginDialogMode.value)

    // For activate, group, and gift flows, show success message instead of redirecting.
    // RM-150 personal-admin is the exception: token-validated, email-confirmed,
    // and account-approved server-side → user is fully registered and logs in directly.
    if (!isPersonalAdminFlow.value
      && (loginDialogVerificationType.value == "activate" || loginDialogVerificationType.value == "group" || loginDialogMode.value == "activate" || isGiftRequestFlow.value)) {
      console.log('Activate, group, or gift flow detected - showing success message')
      formSumbitted.value = true

      return
    }

    console.log('Normal flow - proceeding with login')
    const { userData, userAbilities, jwt } = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    // ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    // C9: validate ?to= against an allowlist before navigating. Keeps the full
    // reload (re-bootstraps the app with the new JWT) but can never run
    // javascript: or redirect off-origin.
    location.href = safeRedirectPath(route.query.to) || '/m/'

    return null
  }).catch(e => {
    console.error('Registration error:', e)
    console.error('Response:', e.response)
    if (e.response && e.response.data) {
      const { errors: formErrors } = e.response.data
      errors.value = formErrors
    }
  })
}

const login = () => {
  let data = {
    email: email.value,
    password: password.value
  }

  //if dummy user pass though dummyEmail
  if (myUser?.value?.userLevel == 'dummy') {
    data.dummyEmail = myUser?.value?.email
  }

  axios.post('/auth/login', data).then(r => {
    const { userData, userAbilities, jwt } = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))

    //ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    // RM-223: a logged-in user is no longer a guest — clear the "resume joining"
    // funnel value so /home never bounces them back to /{group}/activate.
    localStorage.removeItem('guestUserGroup')

    // Everyone lands in member mode after login — platform admins and
    // restaurant admins included. Restaurant Account mode is entered only via
    // the explicit App Mode switcher on the profile screen.
    if (userData?._id) {
      localStorage.setItem(`appMode:${userData._id}`, 'member')
    }

    // C9: validate ?to= against an allowlist before navigating. Full reload
    // re-bootstraps the app with the new JWT; a validated relative path can
    // never run javascript: or redirect off-origin.
    const safeTo = safeRedirectPath(route.query.to)
    if (safeTo)
      location.href = safeTo
    else
      location.href = '/'

    return null
  }).catch(e => {
    console.error(e)
    if (e.response) {
      const { errors: formErrors } = e.response.data
      errors.value = formErrors
    }
  })
}


const removeHomeRedirectPath = () => {
  localStorage.removeItem('homeRedirectPath')
}
</script>

<template>
  <PreferredRequestInfoDialog />

  <!-- 👉 Confirm Dialog -->
  <VDialog
    fullscreen
    :model-value="!!loginDialogMode"
    @update:model-value="updateModelValue"
  >
    <VCard
      class="text-center px-5 pt-4 pb-0"
      style="position: relative; overflow-y: auto;"
    >
      <VBtn
        icon
        variant="text"
        size="small"
        color="secondary"
        style="position: absolute; top: 12px; right: 12px; z-index: 1;"
        @click="onClose"
      >
        <VIcon>mdi-close</VIcon>
      </VBtn>
      <template v-if="formSumbitted">
        <img
          :src="rmLogo"
          width="80"
          class="d-block mx-auto mt-4 mb-4"
        >
        <div class="text-lg font-weight-medium">
          <strong>Thanks for submitting</strong>
        </div>
        <div class="mt-4 text-base">
          We'll follow up within 48 hours
        </div>
        <div class="mt-4 text-base">
          You'll receive an email from preferred@reserverm.com
        </div>
        <VCardActions class="align-center justify-center gap-2 mt-4">
          <VBtn
            variant="elevated"
            color="primary"
            @click="onClose"
          >
            Close
          </VBtn>
        </VCardActions>
      </template>
      <template v-else>
        <div class="mt-0">
          <!-- Login mode header -->
          <h6
            v-if="loginDialogMode == 'login'"
            class="text-lg font-weight-medium mb-2"
          >
            Log in
          </h6>
          <!-- Join mode header -->
          <h6
            v-if="loginDialogMode == 'join'"
            class="text-lg font-weight-medium mb-2"
          >
            Join
          </h6>
          <p
            v-if="loginDialogMode == 'join'"
            class="text-center mb-3"
            style="font-size: 0.95rem;"
          >
            Already a member?
            <a
              class="text-primary font-weight-bold ms-1 cursor-pointer"
              style="text-decoration: underline;"
              @click="loginDialogMode = 'login'; currentStep = 1; showFormComplete = false; formSumbitted = false; clearErrors(); removeHomeRedirectPath();"
            >Log in</a>
          </p>

          <!-- Request to Join header with copy (for activate/gift flows; hidden for personal-admin single-page flow) -->
          <div v-if="(loginDialogMode == 'activate' || isGiftRequestFlow) && !isPersonalAdminFlow">
            <!-- Step 1: Welcome header -->
            <div
              v-if="currentStep === 1"
              class="text-center mb-4"
            >
              <img
                :src="rmLogo"
                width="80"
                class="d-block mx-auto mt-4 mb-4"
              >
              <!--
                <h6 class="text-lg font-weight-medium mb-4 mt-4">
                Thanks for your interest in joining RM
                </h6> 
              -->
              <CmsText
                k="loginJoin.welcome.network"
                tag="p"
                style="font-size: 0.95rem; line-height: 1.4;"
              />
              <CmsText
                k="loginJoin.welcome.benefit"
                tag="p"
                class="mt-4"
                style="font-size: 0.95rem; line-height: 1.4;"
              />
            </div>
            <!-- Steps 2-5: Back button and step indicator on same row -->
            <div
              v-else
              class="d-flex align-center mb-4"
            >
              <VBtn
                icon
                variant="text"
                size="small"
                @click="goToPrevStep"
              >
                <VIcon>mdi-arrow-left</VIcon>
              </VBtn>
              <div class="wizard-step-indicator flex-grow-1">
                <div class="wizard-step-dots">
                  <span
                    v-for="step in wizardStepCount"
                    :key="step"
                    class="wizard-step-dot"
                    :class="{ active: step === currentStep, completed: step < currentStep }"
                  />
                </div>
              </div>
              <!-- Spacer to balance the back button width -->
              <div style="width: 28px;" />
            </div>
          </div>

          <VForm
            ref="refVForm"
            validate-on="blur"
          >
            <!-- ===== STEP 1: Welcome ===== -->
            <div v-if="currentStep === 1 && (loginDialogMode == 'activate' || isGiftRequestFlow) && !isPersonalAdminFlow">
              <div class="text-center py-2">
                <!-- Spouse/partner referral page -->
                <p
                  v-if="loginDialogVerificationType === 'spouse' && guestUserGroup?.referrerFirstName"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  As {{ guestUserGroup.referrerFirstName }}'s spouse, you'll receive a complimentary account with the same Platinum benefits as {{ guestUserGroup.referrerFirstName }}.
                </p>
                <!-- Friend referral page -->
                <p
                  v-else-if="guestUserGroup?.referrerFirstName"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  As {{ guestUserGroup.referrerFirstName }}'s friend, you'll receive 1 month free of Platinum if approved. After that, the Preferred renewal rate is $200/yr.
                </p>
                <!-- Group or Corporate page with custom message -->
                <p
                  v-else-if="guestUserGroup?.personalizedWelcomeMessage"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  {{ guestUserGroup.personalizedWelcomeMessage }}
                </p>
                <!-- Group page (no custom message) -->
                <p
                  v-else-if="loginDialogVerificationType === 'group' && guestUserGroup?.name"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  As a member of {{ guestUserGroup.name }}, you can receive 3 months free of Platinum. Post that, the Preferred renewal rate is $200/yr.
                </p>
                <!-- Corporate paid page (no custom message) -->
                <p
                  v-else-if="loginDialogVerificationType === 'activate' && guestUserGroup?.name"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  As a member of {{ guestUserGroup.name }}, your Platinum membership is provided through them - excited to have you on RM.
                </p>
                <!-- Restaurant page (already-logged-in member: email-in instead of the join wizard) -->
                <CmsText
                  v-else-if="isLoggedInRestaurantRequest"
                  k="loginJoin.restaurant.loggedIn"
                  tag="p"
                  style="font-size: 0.95rem; line-height: 1.4;"
                />
                <!-- Restaurant page (anonymous visitor: runs the join wizard) -->
                <p
                  v-else-if="loginDialogVerificationType === 'restaurant'"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  As a restaurant team member you can activate a free restaurant account that also includes a free Platinum membership
                </p>
                <!--
                  Default (home page, no referral). Hidden for friends/activate/join entry points,
                  where the page-specific welcome above replaces this section. 
                -->
                <CmsText
                  v-else-if="!canRequestToJoin"
                  k="loginJoin.welcome.default"
                  tag="p"
                  style="font-size: 0.95rem; line-height: 1.4;"
                />
              </div>
              <VBtn
                v-if="canRequestToJoin && !isLoggedInRestaurantRequest"
                class="mt-8"
                block
                color="primary"
                size="large"
                rounded="lg"
                @click.prevent="currentStep = 2"
              >
                Request to Join
              </VBtn>
              <p
                class="mb-1 mt-4"
                style="font-size: 0.8rem; line-height: 1.4;"
              >
                <a
                  class="cursor-pointer"
                  @click="isShowingPreferredRequestInfoDialog = true"
                >View how RM Works &amp; FAQs</a>
              </p>
            </div>

            <!-- ===== PERSONAL ADMIN (RM-150): single-page registration ===== -->
            <!--
              Skips the 5-step activate wizard. Invitation token already proves
              email ownership, so the backend bypasses verify-email and logs the
              user in directly on submit. 
            -->
            <div v-if="isPersonalAdminFlow">
              <div class="text-center mb-4">
                <img
                  :src="rmLogo"
                  width="80"
                  class="d-block mx-auto mt-4 mb-4"
                >
                <h6 class="wizard-step-title">
                  Activate your account
                </h6>
                <p
                  v-if="personalAdminInvitation && personalAdminInvitation.memberFirstName"
                  class="wizard-step-subtitle mt-2"
                  style="font-size: 0.95rem; line-height: 1.4;"
                >
                  You're being added as a personal admin on {{ personalAdminInvitation.memberFirstName }}'s account.
                </p>
              </div>
              <ApplicationStepFields
                v-if="applicationForm"
                :form="applicationForm"
                :answers="answers"
                :errors="errors || {}"
                :sms-opt-in="smsOptIn"
                :disabled-keys="['email']"
                :hints="{ email: 'This is the email your invitation was sent to.' }"
                @answer="setAnswer"
                @update:sms-opt-in="v => smsOptIn = v"
              />
              <VProgressLinear
                v-else
                indeterminate
                color="primary"
                class="mb-4"
              />
              <VTextField
                v-model="password"
                autocomplete="new-password"
                :rules="[requiredValidator]"
                :error-messages="errors.password"
                label="Password"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                variant="solo-filled"
                flat
                rounded="lg"
                bg-color="grey-lighten-4"
                density="comfortable"
                class="mb-3"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />

              <div class="d-flex align-start mt-4">
                <VCheckbox
                  id="pa-reviewed-rm"
                  v-model="reviewedRM"
                  inline
                  :rules="[requiredValidator]"
                  hide-details
                  style="margin-top: -4px;"
                />
                <VLabel
                  for="pa-reviewed-rm"
                  style="opacity: 1; white-space: normal; flex: 1;"
                >
                  <span class="me-1">I understand <a
                    class="text-primary cursor-pointer"
                    @click.stop.prevent="isShowingPreferredRequestInfoDialog = true"
                  >how RM works</a></span>
                </VLabel>
              </div>
              <div
                v-if="!reviewedRM"
                class="text-error text-caption ms-8"
              >
                This field is required
              </div>

              <div class="d-flex align-center mt-3">
                <VCheckbox
                  id="pa-privacy-policy"
                  v-model="termsAccepted"
                  inline
                  :rules="[requiredValidator]"
                  :error-messages="errors.termsAccepted"
                />
                <VLabel
                  for="pa-privacy-policy"
                  style="opacity: 1; white-space: normal; flex: 1;"
                >
                  <span class="me-1">I agree to the </span>
                  <RouterLink
                    to="/terms"
                    class="text-primary"
                    @click="loginDialogMode = false;"
                  >
                    terms of service
                  </RouterLink>
                </VLabel>
              </div>

              <p
                v-if="errors.invitationToken && errors.invitationToken.length"
                class="text-error text-caption mt-3"
              >
                {{ errors.invitationToken[0] }}
              </p>

              <VBtn
                class="mt-4"
                block
                color="primary"
                size="large"
                rounded="lg"
                type="submit"
                @click.prevent="onSubmitJoin"
              >
                Activate account
              </VBtn>
            </div>

            <!-- ===== RM-427: the application's screens, from the question library ===== -->
            <div v-if="currentStep >= 2 && (loginDialogMode == 'activate' || isGiftRequestFlow) && !isPersonalAdminFlow">
              <VProgressLinear
                v-if="!applicationForm"
                indeterminate
                color="primary"
                class="my-8"
              />
              <template v-else-if="currentFormStep">
                <div class="text-center mb-4">
                  <h6 class="wizard-step-title">
                    {{ STEP_TITLES[currentFormStep] }}
                  </h6>
                  <p
                    v-if="currentFormStep === 'favorites' && favoritesHint"
                    class="wizard-step-subtitle mt-2"
                  >
                    {{ favoritesHint }}
                  </p>
                  <p
                    v-if="currentFormStep === 'social' && socialAllOptional"
                    class="wizard-step-subtitle mt-2"
                  >
                    Optional: these help us verify identity & expedite review
                  </p>
                </div>

                <ApplicationStepFields
                  :form="applicationForm"
                  :step="currentFormStep"
                  :answers="answers"
                  :errors="errors || {}"
                  :sms-opt-in="smsOptIn"
                  @answer="setAnswer"
                  @update:sms-opt-in="v => smsOptIn = v"
                />

                <template v-if="!isLastStep">
                  <VBtn
                    class="mt-4"
                    block
                    color="primary"
                    size="large"
                    rounded="lg"
                    :loading="checkingEmail"
                    @click.prevent="goToNextStep"
                  >
                    Next
                  </VBtn>
                </template>

                <template v-else>
                  <div class="d-flex align-start mt-4">
                    <VCheckbox
                      id="reviewed-rm"
                      v-model="reviewedRM"
                      inline
                      :rules="[requiredValidator]"
                      hide-details
                      style="margin-top: -4px;"
                    />
                    <VLabel
                      for="reviewed-rm"
                      style="opacity: 1; white-space: normal; flex: 1;"
                    >
                      <span class="me-1">I understand <a
                        class="text-primary cursor-pointer"
                        @click.stop.prevent="isShowingPreferredRequestInfoDialog = true"
                      >how RM works</a></span>
                    </VLabel>
                  </div>
                  <div
                    v-if="!reviewedRM"
                    class="text-error text-caption ms-8"
                  >
                    This field is required
                  </div>

                  <div class="d-flex align-center mt-3">
                    <VCheckbox
                      id="privacy-policy"
                      v-model="termsAccepted"
                      inline
                      :rules="[requiredValidator]"
                      :error-messages="errors.termsAccepted"
                    />
                    <VLabel
                      for="privacy-policy"
                      style="opacity: 1; white-space: normal; flex: 1;"
                    >
                      <span class="me-1">I agree to the </span>
                      <RouterLink
                        to="/terms"
                        class="text-primary"
                        @click="loginDialogMode = false;"
                      >
                        terms of service
                      </RouterLink>
                    </VLabel>
                  </div>

                  <p
                    v-if="errors.signupLink && errors.signupLink.length"
                    class="text-error text-caption mt-3"
                  >
                    {{ errors.signupLink[0] }}
                  </p>
                  <p
                    v-if="serverFieldErrors.length"
                    class="text-error text-caption mt-3"
                  >
                    {{ serverFieldErrors.join(' ') }}
                  </p>

                  <VBtn
                    class="mt-4"
                    block
                    color="primary"
                    size="large"
                    rounded="lg"
                    type="submit"
                    @click.prevent="onSubmitJoin"
                  >
                    Request to Join
                  </VBtn>
                  <p
                    class="mt-4 text-center"
                    style="font-size: 0.85rem; color: #888;"
                  >
                    Questions? email <a href="mailto:preferred@reserverm.com">preferred@reserverm.com</a>
                  </p>
                </template>

                <div
                  v-if="currentFormStep === 'favorites'"
                  class="mt-2"
                >
                  <p
                    class="text-center"
                    style="font-size: 12px;"
                  >
                    Platinum members can submit requests for any restaurant globally unless they've opted out
                  </p>
                </div>
              </template>
            </div>

            <!-- ===== LOGIN MODE ===== -->
            <template v-if="loginDialogMode == 'login'">
              <VTextField
                v-model="email"
                autocomplete="off"
                :rules="[requiredValidator, emailValidator]"
                :error-messages="errors.email"
                label="Email"
                type="email"
                variant="solo-filled"
                flat
                rounded="lg"
                bg-color="grey-lighten-4"
                density="comfortable"
                class="mb-3"
              />
              <VTextField
                v-model="password"
                autocomplete="off"
                :rules="[requiredValidator]"
                :error-messages="errors.password"
                label="Password"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                variant="solo-filled"
                flat
                rounded="lg"
                bg-color="grey-lighten-4"
                density="comfortable"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
              <div class="d-flex align-center flex-wrap justify-space-between mt-2 mb-6">
                <VSpacer />
                <RouterLink
                  class="text-primary ms-2 mb-1"
                  :to="{ name: 'forgot-password' }"
                  @click="loginDialogMode = false;"
                >
                  reset password?
                </RouterLink>
              </div>
              <VBtn
                class="mt-6"
                block
                type="submit"
                @click.prevent="onSubmitLogin"
              >
                Log in
              </VBtn>
              <p class="mt-4">
                new to RM?
                <a
                  class="text-primary ms-2 cursor-pointer"
                  @click="loginDialogMode = 'activate'; currentStep = 1; showFormComplete = false; formSumbitted = false; clearErrors(); removeHomeRedirectPath();"
                >Request
                  to Join
                </a>
              </p>
            </template>

            <!-- ===== JOIN MODE (simple) ===== -->
            <template v-if="loginDialogMode == 'join'">
              <VTextField
                v-model="email"
                autocomplete="off"
                :rules="[requiredValidator, emailValidator]"
                :error-messages="errors.email"
                label="Email"
                type="email"
                class="mb-2"
              />
              <VTextField
                v-model="password"
                autocomplete="off"
                :rules="[requiredValidator]"
                :error-messages="errors.password"
                label="password"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
              <div class="d-flex align-center mt-3">
                <VCheckbox
                  id="privacy-policy-join"
                  v-model="termsAccepted"
                  inline
                  :rules="[requiredValidator]"
                  :error-messages="errors.termsAccepted"
                />
                <VLabel
                  for="privacy-policy-join"
                  style="opacity: 1;"
                >
                  <span class="me-1">I agree to the </span>
                  <RouterLink
                    to="/terms"
                    class="text-primary"
                    @click="loginDialogMode = false;"
                  >
                    terms of service
                  </RouterLink>
                </VLabel>
              </div>
              <p
                v-if="errors.signupLink && errors.signupLink.length"
                class="text-error text-caption mt-3"
              >
                {{ errors.signupLink[0] }}
              </p>
              <VBtn
                class="mt-3"
                block
                type="submit"
                @click.prevent="onSubmitJoin"
              >
                Join
              </VBtn>
            </template>
          </VForm>
        </div>
      </template>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.wizard-step-indicator {
  display: flex;
  justify-content: center;
}

.wizard-step-dots {
  display: flex;
  gap: 8px;
}

.wizard-step-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #E0E0E0;
  transition: all 0.3s ease;

  &.active {
    background-color: var(--ds-brand-primary-dark);
    width: 24px;
    border-radius: 4px;
  }

  &.completed {
    background-color: var(--ds-brand-primary-dark);
  }
}

.wizard-step-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ds-text-strong);
}

.wizard-step-subtitle {
  font-size: 0.9rem;
  color: var(--ds-text-muted);
  line-height: 1.5;
}
</style>

